import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  LinearProgress,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import { useQuery } from "react-query";
import API from "../../../../../API";
import {
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Refresh,
  Send,
  ViewList,
  BugReport,
  Search,
  ForwardToInbox,
} from "@mui/icons-material";

import { Switch } from "@mui/material";

import client from "../../../../../../src/API/index";

import FormControlLabel from "@mui/material/FormControlLabel";
function PartnerStatusCard({ creator, onProcess, onViewEmails, onDebug }) {
  /**
   * @typedef {Object} creator
   * @property {string} last_email_check - ISO timestamp
   * @property {string} last_email_update - ISO timestamp
   * * @typedef {Object} PartnerConfiguration
   * @property {string|null} forwarded_alias
   * @property {boolean} gmail_connected
   * @property {boolean} has_refresh_token
   * @property {boolean} need_reauth
   * @property {boolean} process_emails
   * * @typedef {Object} Creator
   * @property {string} display_name
   * @property {string} username
   * @property {string} email
   * @property {string} status - e.g., "inactive"
   * @property {number} health_score - Float between 0 and 1
   * @property {boolean} ai_negotiations_bool
   * @property {PartnerActivity} activity
   * @property {PartnerConfiguration} configuration
   * @property {string[]} issues - Array of issue codes
   */

  const debounceTimerRef = useRef(null);

  // 2. Cleanup function: If the user navigates away from the page before
  // the timer finishes, this prevents the app from trying to update an unmounted component.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);
  const getHealthColor = (score) => {
    if (score >= 0.8) return "success";
    if (score >= 0.5) return "warning";
    return "error";
  };

  const getHealthIcon = (score) => {
    if (score >= 0.8) return <CheckCircle color="success" />;
    if (score >= 0.5) return <Warning color="warning" />;
    return <ErrorIcon color="error" />;
  };

  const [aiBooleanIndicator, setAiBooleanIndicator] = useState(() => {
    if (creator != null) {
      return creator.ai_negotiations_bool;
    }
    return false;
  });

  //Later use a debounce function
  const handleAIToggle = (creator_id, new_ai_negotiations_status) => {
    setAiBooleanIndicator(new_ai_negotiations_status);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const res = await client.admin.updateAiNegotiationsValueForCreator(
          creator_id,
          new_ai_negotiations_status,
        );

        if (res.message.toLowerCase() !== "success") {
          throw new Error("Backend did not return 'ok'");
        }

        // Success!
        // -> Do your API calls to update the parent creator object here if needed.
        // updateParentCreatorState(res.data);
      } catch (error) {
        console.error("Failed to update AI negotiations:");

        setAiBooleanIndicator(!new_ai_negotiations_status);
      }
    }, 500); // <-- 500ms debounce delay. Adjust as you see fit!
  };

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6">{creator.display_name}</Typography>
            <Typography variant="caption" color="text.secondary">
              @{creator.username}
            </Typography>
          </Box>
          {getHealthIcon(creator.health_score)}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Health Score
          </Typography>
          <LinearProgress
            variant="determinate"
            value={creator.health_score * 100}
            color={getHealthColor(creator.health_score)}
            sx={{ height: 8, borderRadius: 1, mt: 0.5 }}
          />
        </Box>

        <Chip
          label={creator.status === "active" ? "Active" : "Inactive"}
          color={creator.status === "active" ? "success" : "default"}
          size="small"
          sx={{ mb: 1 }}
        />

        {creator.issues.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="error">
              Issues:
            </Typography>
            <List dense>
              {creator.issues.map((issue, idx) => (
                <ListItem key={idx} sx={{ py: 0 }}>
                  <ListItemText
                    primary={issue.replace(/_/g, " ")}
                    primaryTypographyProps={{ variant: "caption" }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={aiBooleanIndicator}
                onChange={(event) =>
                  handleAIToggle(creator.username, event.target.checked)
                }
                size="small"
                color="primary"
              />
            }
            label={
              <Typography
                variant="caption"
                color="text.secondary"
                className="span"
              >
                AI Processing
              </Typography>
            }
          />

          <Typography variant="caption" color="text.secondary">
            Forwarding: {creator.configuration.forwarded_alias ? "ON" : "OFF"}
          </Typography>
        </Box>
      </CardContent>

      <CardActions sx={{ flexDirection: "column", gap: 1, px: 2, pb: 2 }}>
        <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
          <Button
            size="small"
            startIcon={<Refresh />}
            onClick={() => onProcess([creator.username], "check")}
            disabled={creator.configuration.need_reauth}
            sx={{ flex: 1 }}
          >
            Check
          </Button>
          <Button
            size="small"
            startIcon={<Send />}
            onClick={() => onProcess([creator.username], "process")}
            disabled={
              creator.configuration.need_reauth ||
              !creator.configuration.process_emails
            }
            sx={{ flex: 1 }}
          >
            Process
          </Button>
        </Box>
        <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            startIcon={<ViewList />}
            onClick={() => onViewEmails(creator.username)}
          >
            View Emails
          </Button>
          <Tooltip title="Debug email check">
            <IconButton
              size="small"
              color="info"
              onClick={() => onDebug(creator)}
              sx={{
                border: "1px solid",
                borderColor: "info.main",
                borderRadius: 1,
              }}
            >
              <BugReport fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardActions>
    </Card>
  );
}

export default function PartnerManagementTab({
  onPartnerProcess,
  onViewEmails,
  onDebug,
  showToast,
}) {
  const [partnerSearch, setPartnerSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [healthFilter, setHealthFilter] = useState("all");
  const [aiProcessingFilter, setAiProcessingFilter] = useState("all");
  const [forwardingFilter, setForwardingFilter] = useState("all");
  const [sortBy, setSortBy] = useState("health");
  const [sortDirection, setSortDirection] = useState("asc");

  const {
    data: partnerStatus,
    isLoading: loadingPartners,
    refetch: refetchPartners,
  } = useQuery({
    queryKey: ["partnerStatus"],
    queryFn: () => API.blitzAI.getPartnerStatus(false),
    refetchInterval: false,
    onError: (error) => {
      showToast(error.response?.data?.error || "Failed to load partner status");
    },
  });

  const getFilteredAndSortedPartners = () => {
    if (!partnerStatus?.creators) return [];

    let filtered = [...partnerStatus.creators];

    if (partnerSearch) {
      const searchLower = partnerSearch.toLowerCase();
      filtered = filtered.filter(
        (creator) =>
          creator.username.toLowerCase().includes(searchLower) ||
          creator.display_name.toLowerCase().includes(searchLower) ||
          creator.email?.toLowerCase().includes(searchLower),
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((creator) => creator.status === statusFilter);
    }

    if (healthFilter !== "all") {
      filtered = filtered.filter((creator) => {
        if (healthFilter === "healthy") return creator.health_score >= 0.8;
        if (healthFilter === "warning")
          return creator.health_score >= 0.5 && creator.health_score < 0.8;
        if (healthFilter === "critical") return creator.health_score < 0.5;
        return true;
      });
    }

    if (aiProcessingFilter !== "all") {
      const isEnabled = aiProcessingFilter === "enabled";
      filtered = filtered.filter(
        (creator) => creator.configuration.process_emails === isEnabled,
      );
    }

    if (forwardingFilter !== "all") {
      const isEnabled = forwardingFilter === "enabled";
      filtered = filtered.filter((creator) =>
        isEnabled
          ? !!creator.configuration.forwarded_alias
          : !creator.configuration.forwarded_alias,
      );
    }

    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case "health":
          aVal = a.health_score;
          bVal = b.health_score;
          break;
        case "username":
          aVal = a.username.toLowerCase();
          bVal = b.username.toLowerCase();
          break;
        case "displayName":
          aVal = a.display_name.toLowerCase();
          bVal = b.display_name.toLowerCase();
          break;
        case "lastCheck":
          aVal = a.activity.last_email_check
            ? new Date(a.activity.last_email_check).getTime()
            : 0;
          bVal = b.activity.last_email_check
            ? new Date(b.activity.last_email_check).getTime()
            : 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const filteredPartners = getFilteredAndSortedPartners();
  console.log("filter partners: ", filteredPartners);

  const handleResetFilters = () => {
    setPartnerSearch("");
    setStatusFilter("all");
    setHealthFilter("all");
    setAiProcessingFilter("all");
    setForwardingFilter("all");
    setSortBy("health");
    setSortDirection("asc");
  };

  if (!partnerStatus) return null;

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Partners
              </Typography>
              <Typography variant="h4">
                {partnerStatus.total_partners}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active
              </Typography>
              <Typography variant="h4" color="success.main">
                {partnerStatus.active_partners}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Needs Attention
              </Typography>
              <Typography variant="h4" color="warning.main">
                {partnerStatus.summary.needs_reauth +
                  partnerStatus.summary.missing_tokens}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Healthy
              </Typography>
              <Typography variant="h4" color="success.main">
                {partnerStatus.summary.healthy}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Filter & Sort</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                startIcon={<Refresh />}
                onClick={handleResetFilters}
              >
                Reset
              </Button>
              <Button
                size="small"
                startIcon={<Refresh />}
                onClick={() => refetchPartners()}
                disabled={loadingPartners}
              >
                Refresh Data
              </Button>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              Quick Filters:
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ErrorIcon />}
                onClick={() => {
                  handleResetFilters();
                  setHealthFilter("critical");
                  setSortBy("health");
                  setSortDirection("asc");
                }}
              >
                Critical Health
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Warning />}
                onClick={() => {
                  handleResetFilters();
                  setStatusFilter("inactive");
                }}
              >
                Needs Attention
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CheckCircle />}
                onClick={() => {
                  handleResetFilters();
                  setHealthFilter("healthy");
                  setStatusFilter("active");
                }}
              >
                Healthy & Active
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  handleResetFilters();
                  setAiProcessingFilter("disabled");
                }}
              >
                AI Disabled
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ForwardToInbox />}
                onClick={() => {
                  handleResetFilters();
                  setForwardingFilter("enabled");
                }}
              >
                Has Forwarding
              </Button>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Partners"
                placeholder="Search by username, name, or email..."
                value={partnerSearch}
                onChange={(e) => setPartnerSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Health</InputLabel>
                <Select
                  value={healthFilter}
                  onChange={(e) => setHealthFilter(e.target.value)}
                  label="Health"
                >
                  <MenuItem value="all">All Health</MenuItem>
                  <MenuItem value="healthy">Healthy (≥80%)</MenuItem>
                  <MenuItem value="warning">Warning (50-80%)</MenuItem>
                  <MenuItem value="critical">Critical (&lt;50%)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3}>
              <FormControl fullWidth>
                <InputLabel>AI Processing</InputLabel>

                <Select
                  value={aiProcessingFilter}
                  onChange={(e) => setAiProcessingFilter(e.target.value)}
                  label="AI Processing"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="enabled">Enabled</MenuItem>
                  <MenuItem value="disabled">Disabled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Forwarding</InputLabel>
                <Select
                  value={forwardingFilter}
                  onChange={(e) => setForwardingFilter(e.target.value)}
                  label="Forwarding"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="enabled">Enabled</MenuItem>
                  <MenuItem value="disabled">Disabled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="health">Health Score</MenuItem>
                  <MenuItem value="username">Username</MenuItem>
                  <MenuItem value="displayName">Display Name</MenuItem>
                  <MenuItem value="lastCheck">Last Check</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  value={sortDirection}
                  onChange={(e) => setSortDirection(e.target.value)}
                  label="Direction"
                >
                  <MenuItem value="asc">
                    {sortBy === "health"
                      ? "Worst First"
                      : sortBy === "lastCheck"
                        ? "Oldest First"
                        : "A-Z"}
                  </MenuItem>
                  <MenuItem value="desc">
                    {sortBy === "health"
                      ? "Best First"
                      : sortBy === "lastCheck"
                        ? "Newest First"
                        : "Z-A"}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {filteredPartners.length} of{" "}
              {partnerStatus.total_partners} partners
            </Typography>
            {filteredPartners.length === 0 && (
              <Alert severity="info" sx={{ py: 0 }}>
                No partners match the current filters
              </Alert>
            )}
          </Box>

          {(partnerSearch ||
            statusFilter !== "all" ||
            healthFilter !== "all" ||
            aiProcessingFilter !== "all" ||
            forwardingFilter !== "all") && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Active Filters:
              </Typography>
              {partnerSearch && (
                <Chip
                  size="small"
                  label={`Search: ${partnerSearch}`}
                  onDelete={() => setPartnerSearch("")}
                />
              )}
              {statusFilter !== "all" && (
                <Chip
                  size="small"
                  label={`Status: ${statusFilter}`}
                  onDelete={() => setStatusFilter("all")}
                />
              )}
              {healthFilter !== "all" && (
                <Chip
                  size="small"
                  label={`Health: ${healthFilter}`}
                  onDelete={() => setHealthFilter("all")}
                />
              )}
              {aiProcessingFilter !== "all" && (
                <Chip
                  size="small"
                  label={`AI: ${aiProcessingFilter}`}
                  onDelete={() => setAiProcessingFilter("all")}
                />
              )}
              {forwardingFilter !== "all" && (
                <Chip
                  size="small"
                  label={`Forwarding: ${forwardingFilter}`}
                  onDelete={() => setForwardingFilter("all")}
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {filteredPartners.length > 0 && (
        <Grid container spacing={2}>
          {filteredPartners.map((creator) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={creator.username}>
              <PartnerStatusCard
                creator={creator}
                onProcess={onPartnerProcess}
                onViewEmails={onViewEmails}
                onDebug={onDebug}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
